import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, LogOut, Inbox, Mail, Phone, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import AdminLoginCard from '../components/sections/AdminLoginCard';
import { useAdminSession } from '../hooks/useAdminSession';
import { HTTP } from '../lib/httpStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => {
  try {
    return new Date(value).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
};

const SubmissionRow = ({ submission }) => (
  <Card className="border-2" data-testid="admin-submission-row">
    <CardContent className="p-6 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-900">{submission.name}</h3>
        <span className="text-xs text-gray-500 flex items-center space-x-1">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(submission.submitted_at)}</span>
        </span>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
        <a
          href={`mailto:${submission.email}`}
          className="flex items-center space-x-1 hover:text-blue-600"
        >
          <Mail className="w-4 h-4" />
          <span>{submission.email}</span>
        </a>
        <a
          href={`tel:${submission.phone}`}
          className="flex items-center space-x-1 hover:text-blue-600"
        >
          <Phone className="w-4 h-4" />
          <span>{submission.phone}</span>
        </a>
      </div>
      <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-md p-3 text-sm">
        {submission.message}
      </p>
    </CardContent>
  </Card>
);

const AdminSubmissions = () => {
  const { isLoggedIn, login, logout, authedFetch } = useAdminSession();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadSubmissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await authedFetch({
        url: `${BACKEND_URL}/api/contact/submissions`,
        method: 'GET',
      });
      setSubmissions(data.submissions || []);
    } catch (error) {
      if (error.response?.status !== HTTP.UNAUTHORIZED) toast.error('Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (isLoggedIn) loadSubmissions();
  }, [isLoggedIn, loadSubmissions]);

  if (!isLoggedIn) {
    return (
      <AdminLoginCard
        onLogin={login}
        subtitle="Enter the admin password to view contact form submissions."
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 rounded-full p-3">
              <Inbox className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contact Submissions</h1>
              <p className="text-sm text-gray-600">
                {submissions.length} {submissions.length === 1 ? 'entry' : 'entries'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={loadSubmissions}
              disabled={isLoading}
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>

        {isLoading && submissions.length === 0 && (
          <p className="text-center text-gray-500 py-10">Loading submissions…</p>
        )}

        {!isLoading && submissions.length === 0 && (
          <Card className="border-2">
            <CardContent className="p-10 text-center text-gray-500">
              No submissions yet. New contact form entries will appear here.
            </CardContent>
          </Card>
        )}

        <div className="space-y-4" data-testid="admin-submissions-list">
          {submissions.map((s) => (
            <SubmissionRow key={s.id} submission={s} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminSubmissions;
